"use client";

import { Core } from "@faire/design-tokens";
import { Button } from "@faire/web/slate-deprecated/Button";
import { ButtonProps } from "@faire/web/slate-deprecated/Button/types";
import { IconButton } from "@faire/web/slate-deprecated/IconButton";
import { IconButtonProps } from "@faire/web/slate-deprecated/IconButton/types";
import { Row, Column } from "@faire/web/slate/Layout";
import { IHighlightColor } from "@faire/web/slate/Theme";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { TypographyStyles } from "@faire/web/slate/Typography";
import { TypographyProps as ITypography } from "@faire/web/slate/Typography/Types";
import { cn } from "@faire/web/tools/cn";
import { Link } from "@faire/web/ui/LinkAnchor";
import * as React from "react";
import { LinkProps } from "react-router-dom";
import styled, { css, useTheme } from "styled-components";

export const PAGE_LINK_SIZE = 32;
export const PAGE_LINK_MARGIN = 8;
export const DESKTOP_NEXT_LINK_HEIGHT = 24;
export const DESKTOP_NEXT_LINK_MIN_WIDTH = 100;
const DESKTOP_NEXT_LINK_MAX_WIDTH = 144;
export const DESKTOP_NEXT_LINK_MARGIN = 32;
export const NEXT_LINK_SIZE = 48;
const XXS_NEXT_LINK_SIZE = 32;
export const NEXT_LINK_MARGIN = 32;
export const MOBILE_NEXT_LINK_MARGIN = 16;
/* When the screensize is less than 390px, removing the margin for navigation buttons to fit the screen. */
const XS_SCREEN_WIDTH = 390;
/* When the screensize is less than 344px, removing the margin for navigation buttons is not enough,
 * The button width will also be reduced to 32px.
 */
const XXS_SCREEN_WIDTH = 344;

export const FooterWrapper = styled(Column).attrs({
  align: "center",
})`
  width: 100%;
`;

export const Wrapper = styled(Row).attrs({
  justify: "center",
  align: "center",
})`
  width: 100%;
  flex-shrink: 0;
`;

export const DesktopWrapper = styled(Wrapper)`
  &&& {
    ${responsiveMediaQueries.mobile} {
      display: none;
    }
  }
`;

export const MobileWrapper = styled(Wrapper)`
  &&& {
    ${responsiveMediaQueries.tabletAndAbove} {
      display: none;
    }
  }
`;

export const DesktopButton = styled(Button)<ButtonProps>`
  max-width: ${DESKTOP_NEXT_LINK_MAX_WIDTH}px;
  min-width: ${DESKTOP_NEXT_LINK_MIN_WIDTH}px;
  width: 100%;
  height: ${DESKTOP_NEXT_LINK_HEIGHT}px;
  line-height: 18px;
  margin: 0 ${DESKTOP_NEXT_LINK_MARGIN}px;
`;

export const MobileButton = styled(Button)<ButtonProps>`
  width: 100%;
  height: ${PAGE_LINK_SIZE}px;
`;

export const PageWrapper = styled(Row).attrs({
  justify: "center",
  align: "center",
})``;

interface IPageLinkProps {
  active?: boolean;
  disabled?: boolean;
  "data-test-id"?: string;
}

export const TextPageLink: React.FC<IPageLinkProps & LinkProps> = (props) => {
  const { active, className, ...otherProps } = props;
  const { pagination } = useTheme();
  const pageColors: IHighlightColor = active
    ? pagination.activePage
    : pagination.inactivePage;
  const text = pagination.text;
  return (
    <CircleLink
      {...otherProps}
      className={cn(className, active ? "active" : "inactive")}
      colors={pageColors}
      text={text}
      width={PAGE_LINK_SIZE}
      margin={PAGE_LINK_MARGIN}
    />
  );
};

export const IconPageLink: React.FC<IPageLinkProps & LinkProps> = (props) => {
  const { active, className, ...otherProps } = props;
  const { pagination } = useTheme();
  const pageColors: IHighlightColor = active
    ? pagination.activePage
    : pagination.inactivePage;
  const text = pagination.text;
  return (
    <IconButtonCircleLink
      {...otherProps}
      className={cn(className, active ? "active" : "inactive")}
      colors={pageColors}
      text={text}
      width={PAGE_LINK_SIZE}
      margin={PAGE_LINK_MARGIN}
      tabIndex={otherProps.disabled ? -1 : 0}
    />
  );
};

const CircleLink = styled(Link)<
  {
    colors: IHighlightColor;
    text: ITypography;
    width: number;
    margin: number;
    disabled?: boolean;
  } & LinkProps
>`
  ${({ colors, text, width, margin, disabled }) => css`
    min-width: ${width}px;
    padding: 6px;
    height: ${width}px;
    margin: 0 ${margin}px !important;
    ${TypographyStyles(text)}

    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100px;
    background-color: ${colors.color};

    &,
    &:focus,
    &:active {
      color: ${colors.contrast};
    }

    &:hover {
      background-color: ${colors.hover};
    }

    ${disabled ? "pointer-events: none; cursor: none" : ""};
  `}
`;

const IconButtonCircleLink = styled(CircleLink)`
  &:hover {
    border: 1px solid ${Core.border.default};
  }
  &:focus-visible {
    outline: 1px solid -webkit-focus-ring-color;
  }
`;

export const NavigationButton = styled(IconButton).attrs({
  shape: "square",
})<IconButtonProps>`
  width: ${NEXT_LINK_SIZE}px;
  height: ${NEXT_LINK_SIZE}px;
  margin: 0 ${NEXT_LINK_MARGIN}px;
  @media (max-width: 767px) {
    margin: 0 ${MOBILE_NEXT_LINK_MARGIN}px;
  }
  @media (max-width: ${XS_SCREEN_WIDTH}px) {
    margin: 0;
  }
  @media (max-width: ${XXS_SCREEN_WIDTH}px) {
    width: ${XXS_NEXT_LINK_SIZE}px;
    height: ${XXS_NEXT_LINK_SIZE}px;
  }
`;
